import useMetrics from "../../../hooks/useMetrics"
import { useTypedSelector } from "../../../hooks/useTypedSelector"
import { applyBidChangeLimits, minMaxValidation } from "../../../hooks/helpers/metrics.helper"
import {
  canRaise,
  clamp,
  gateRaise,
  highestMain,
  lowestMain,
  mainModifierPct,
  MODIFIER_MAX,
  MODIFIER_MIN,
  resolveTargetCpc,
  round2,
} from "../../../lib/bidCore"
import { IPlacementData, IPlacementDataTable, TargetAcosMode } from "../types/placement"
import { placementsEnum } from "../types/types"
import { copy } from "../../../helper/system.helpers.functions"
import { IPlacementDataForUpdate } from "../store/types/placements"
import { ICampaign, IDefaultTargetAcos } from "../types/sponsoredProducts"
import { matchTypesFromAmazon } from "../../../types/customers"
import { selectCampaignIfNeeded } from "./helpers/placementMetrics.helper"
import { findDefaultTargetAcos, resolveMaxTargetCpc } from "./helpers/targetAcos.helper"
import { findAovValue } from "./helpers/aov.helper"

const MAIN_PLACEMENTS: string[] = [
  placementsEnum.TOP_OF_SEARCH,
  placementsEnum.REST_OF_SEARCH,
  placementsEnum.PRODUCT_PAGE,
]

interface ICalcLimitedCPCForPlacement {
  placements: IPlacementDataTable[]
  minLimitedCPC: number
}

interface IResolvedPlacementBid {
  targetCPC: number
  limitedCPC: number
  hold: boolean
}

interface IUsePlacementMetricsProps {
  setPlacementsDataForUpdate: React.Dispatch<React.SetStateAction<IPlacementDataForUpdate[]>>
  setSelectedCampaignIds: React.Dispatch<React.SetStateAction<number[]>>
  setMinLimitedCPCByCampaignIdMap: React.Dispatch<React.SetStateAction<Record<string, number>>>
}

function isMainPlacement(placement: IPlacementDataTable): boolean {
  return MAIN_PLACEMENTS.includes(placement.placement)
}

const FLOAT_EPSILON = 1e-9

export const DEFAULT_DYNAMIC_ACOS_MIN_MARKET_PURCHASES = 200

function targetAcosMode(
  ladderHold: boolean,
  dynamicEnabled: boolean | undefined,
  volumeOk: boolean
): TargetAcosMode {
  if (ladderHold) return "fixed-ladder"
  if (!dynamicEnabled) return "fixed"
  return volumeOk ? "dynamic" : "fixed-low-volume"
}

function capAt(cpc: number, maxCpc: number): number {
  // A cap of 0/unset is still a hard ceiling (never uncapped); Apply blocks such campaigns.
  return Math.min(cpc, maxCpc > 0 ? maxCpc : 0)
}

/** A7: may not be raised (< 3 orders or < 10 clicks) AND has a live bid to hold at. */
function isRaiseGated(placement: IPlacementDataTable): boolean {
  return (
    !canRaise({ orders: placement.orders, clicks: placement.clicks }) &&
    Number(placement.targetCPCAmazon) > 0
  )
}

/** A7: largest modifier <= pct with base × (1 + pct/100) <= cap (never below 0). */
function capModifierAtCpc(pct: number, base: number, cap: number): number {
  if (!(base > 0) || !(cap > 0)) return pct
  return Math.max(MODIFIER_MIN, Math.min(pct, Math.floor((cap / base - 1) * 100 + FLOAT_EPSILON)))
}

/** Featured Videos is multiplied by the BASE (keyword) bid, not by the highest main placement. */
function isBaseStacked(placement: IPlacementDataTable): boolean {
  return placement.placement === placementsEnum.FEATURED_VIDEOS
}

/** A7: lowest bid of a raise-gated AUDIENCE / AB row (stacks on the highest main) — no main may rise above it. */
function gatedAuxCap(placements: IPlacementDataTable[]): number | null {
  return lowestMain(
    placements
      .filter(
        (placement) =>
          !isMainPlacement(placement) && !isBaseStacked(placement) && isRaiseGated(placement)
      )
      .map((placement) => placement.limitedCPC || 0)
      .filter((cpc) => cpc > 0)
  )
}

/** A7: lowest bid of a raise-gated base-stacked row (FV) — the new base may not exceed it. */
function gatedBaseStackedCap(placements: IPlacementDataTable[]): number | null {
  return lowestMain(
    placements
      .filter((placement) => isBaseStacked(placement) && isRaiseGated(placement))
      .map((placement) => placement.limitedCPC || 0)
      .filter((cpc) => cpc > 0)
  )
}

/**
 * Anton 2026-10-18: a placement WITHOUT enough data (< 3 orders or < 10 clicks — the same A7 rule as raises)
 * may carry at most the DEFAULT placement multiplier from the rankattack.com PPC settings (Settings -> Other):
 * TOS = TOSPercentage, Rest of Search = ROSPercentage, Product pages = PPPercentage, Amazon Business =
 * ABPercentage, Audience = ADPercentage. Featured Video has no .com setting -> 10%. Lower computed values stay
 * lower; only placements with >= 3 orders AND >= 10 clicks may go above the default, from their own data.
 * Stops a held placement from keeping an old, high effective bid as a huge multiplier when the base bid is cut
 * (e.g. Product pages +362% on a $1.05 base). The constants below are fallbacks when a setting is missing.
 */
export const THIN_DATA_MAX_MODIFIER_PCT: Record<string, number> = {
  [placementsEnum.TOP_OF_SEARCH]: 50,
  [placementsEnum.REST_OF_SEARCH]: 25,
  [placementsEnum.PRODUCT_PAGE]: 0,
  [placementsEnum.AUDIENCE]: 10,
  [placementsEnum.AMAZON_BUSINESS]: 10,
  [placementsEnum.FEATURED_VIDEOS]: 10,
}

export interface IThinDataDefaults {
  TOSPercentage?: number | null
  ROSPercentage?: number | null
  PPPercentage?: number | null
  ABPercentage?: number | null
  ADPercentage?: number | null
}

function settingPct(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null
}

export function thinDataMaxModifier(
  placementName: string,
  defaults: IThinDataDefaults | null = null
): number | undefined {
  const fromSettings: Record<string, number | null | undefined> = {
    [placementsEnum.TOP_OF_SEARCH]: defaults?.TOSPercentage,
    [placementsEnum.REST_OF_SEARCH]: defaults?.ROSPercentage,
    [placementsEnum.PRODUCT_PAGE]: defaults?.PPPercentage,
    [placementsEnum.AMAZON_BUSINESS]: defaults?.ABPercentage,
    [placementsEnum.AUDIENCE]: defaults?.ADPercentage,
  }
  return settingPct(fromSettings[placementName]) ?? THIN_DATA_MAX_MODIFIER_PCT[placementName]
}

function isThinData(placement: IPlacementDataTable): boolean {
  return !canRaise({ orders: placement.orders, clicks: placement.clicks })
}

/** Cap a computed modifier of a thin-data placement at its default (never raises a lower value). */
function capThinModifier(
  placement: IPlacementDataTable,
  pct: number,
  defaults: IThinDataDefaults | null = null
): number {
  const max = thinDataMaxModifier(placement.placement, defaults)
  return max !== undefined && isThinData(placement) ? Math.min(pct, max) : pct
}

function floorCents(cpc: number): number {
  return Math.floor(cpc * 100 + FLOAT_EPSILON) / 100
}

function flooredModifierPct(placementCpc: number | undefined, baseCpc: number | null): number {
  if (!baseCpc || baseCpc <= 0) return mainModifierPct(placementCpc, baseCpc)
  const pct = Math.floor(((placementCpc || 0) / baseCpc - 1) * 100 + FLOAT_EPSILON)
  return clamp(pct, MODIFIER_MIN, MODIFIER_MAX)
}

const usePlacementMetrics = (params: IUsePlacementMetricsProps) => {
  const { setPlacementsDataForUpdate, setSelectedCampaignIds, setMinLimitedCPCByCampaignIdMap } =
    params

  const { campaignData } = useTypedSelector((select) => select.sponsoredProductsSKC)

  const { calcBestCPC } = useMetrics()

  function getAovValue(campaignName: string) {
    return findAovValue(campaignData.getAOVData?.aovs, campaignName)
  }

  function findCampaign(campaignId: number) {
    return campaignData.campaigns.find((campaign) => campaign.campaignId === campaignId)
  }

  function maxCpcForCampaign(campaignId: number): number {
    return resolveMaxTargetCpc(
      findCampaign(campaignId),
      campaignData.maxTargetCPC,
      campaignData.maxTargetCPCByType
    )
  }

  function capLimitedCPC(campaignId: number, cpc: number): number {
    return capAt(cpc, maxCpcForCampaign(campaignId))
  }

  function isBestCpcFromApi(placement: IPlacementDataTable): boolean {
    return placement.placement === placementsEnum.FEATURED_VIDEOS
  }

  function calcBestCPCForPlacement(placements: IPlacementDataTable[]): IPlacementDataTable[] {
    const placementsCopy: IPlacementDataTable[] = copy(placements)

    for (const placement of placementsCopy) {
      if (isBestCpcFromApi(placement)) {
        continue
      }
      placement.bestCPC = calcBestCPC({
        targetCVRCPC: placement.targetCVRCPC,
        targetCPC: placement.targetCPC || 0,
        bestCVRCPC: placement.bestCVRCPC,
      })
    }

    return placementsCopy
  }

  function resolvePlacementBid(
    placement: IPlacementDataTable,
    aovValue: number,
    maxTargetCPC: number
  ): IResolvedPlacementBid {
    const currentBid = placement.targetCPCAmazon || 0
    const resolved = resolveTargetCpc({
      cpc: placement.clicks ? placement.spend / placement.clicks : 0,
      targetAcos: placement.targetAcos,
      actualAcos: placement.acos,
      orders: placement.orders,
      clicks: placement.clicks,
      aov: aovValue || undefined,
      currentBid: currentBid || undefined,
    })
    const targetCPC = round2(resolved.cpc ?? campaignData.defaultBid)

    // A7: "raise-gated" (a raise on < 3 orders or < 10 clicks) holds like A4.
    if (resolved.rule === "hold" || resolved.rule === "raise-gated") {
      // A7: keep the UNROUNDED live bid as the held ceiling (round2 could lift $0.6095 to $0.61)
      const held = currentBid > 0 ? Math.min(targetCPC, currentBid) : targetCPC
      return { targetCPC, limitedCPC: capAt(held, maxTargetCPC), hold: true }
    }

    const limitedCPC = currentBid
      ? applyBidChangeLimits({
          bidInAmazon: currentBid,
          targetCPC,
          currentAcos: placement.acos,
          conditions: campaignData.limitedCPCValue?.conditions || [],
          minTargetCPC: campaignData.minTargetCPC,
          maxTargetCPC,
        })
      : minMaxValidation(targetCPC, campaignData.minTargetCPC, maxTargetCPC)
    // A7: not even the min-CPC floor may lift a thin-data placement over its live bid.
    return {
      targetCPC,
      limitedCPC: gateRaise(limitedCPC, currentBid, placement),
      hold: false,
    }
  }

  function heldKeywordBid(campaign: ICampaign | undefined): number {
    return campaign?.keywordBid || campaignData.defaultBid
  }

  function calcLimitedCPCForPlacement(
    placements: IPlacementDataTable[],
    campaignId: number
  ): ICalcLimitedCPCForPlacement {
    const campaign = findCampaign(campaignId)
    const aovValue = getAovValue(campaign?.campaignName || "")
    const placementsCopy: IPlacementDataTable[] = copy(placements)
    const activeMains: IPlacementDataTable[] = []
    const maxTargetCPC = maxCpcForCampaign(campaignId)

    for (const placement of placementsCopy) {
      const resolved = resolvePlacementBid(placement, aovValue, maxTargetCPC)
      placement.targetCPC = resolved.targetCPC
      placement.limitedCPC = resolved.limitedCPC
      if (isMainPlacement(placement) && !resolved.hold) {
        activeMains.push(placement)
      }
    }

    // A7: an aux effective bid is highestMain × (1 + pct >= 0), so no main may rise above
    // the live bid of a raise-gated aux row (a main is never pushed below its own live bid).
    const auxCap = gatedAuxCap(placementsCopy)
    const mains = placementsCopy.filter(isMainPlacement)
    if (auxCap != null) {
      for (const placement of mains) {
        placement.limitedCPC = Math.min(
          placement.limitedCPC || 0,
          Math.max(auxCap, Number(placement.targetCPCAmazon) || 0)
        )
      }
    }

    let minLimitedCPC = capAt(
      lowestMain(activeMains.map((placement) => placement.limitedCPC)) ?? heldKeywordBid(campaign),
      maxTargetCPC
    )
    // A7 (NEW-SP-AUX-BASE-ROUNDING): an aux ceiling can leave a fractional-cent base ($0.6095);
    // Apply pushes 2 dp, so floor it here or round-half-up would lift it over the gated aux bid.
    if (auxCap != null) {
      minLimitedCPC = floorCents(minLimitedCPC)
    }
    // A7: main modifiers are >= 0 so effective >= base: the base may not exceed the bid of a
    // main that may not rise (held rows too); floored to cents because Apply pushes 2 dp.
    const gatedMainCap = lowestMain(
      mains.filter(isRaiseGated).map((placement) => placement.limitedCPC || 0)
    )
    if (gatedMainCap != null && gatedMainCap > 0) {
      minLimitedCPC = floorCents(Math.min(minLimitedCPC, gatedMainCap))
    }
    // A7 (FV): Featured Videos multiplies the base with a modifier >= 0, so a thin-data FV row
    // caps the base itself (its effective bid = base × (1 + pct) may not rise above its live bid).
    const gatedFvCap = gatedBaseStackedCap(placementsCopy)
    if (gatedFvCap != null && gatedFvCap > 0) {
      minLimitedCPC = floorCents(Math.min(minLimitedCPC, gatedFvCap))
    }

    return { placements: placementsCopy, minLimitedCPC }
  }

  function calcTargetACOS(placements: IPlacementDataTable[]) {
    const placementsCopy: IPlacementDataTable[] = copy(placements)

    for (const placement of placementsCopy) {
      const campaign = campaignData.campaigns.find(
        (campaign) => campaign.campaignId === placement.campaignId
      )
      const targetAcos = determineTargetAcos(campaign)
      const volumeOk = hasDynamicAcosVolume(campaign)
      const ladderHold = campaign?.ladderFixedAcos?.active === true
      const ladderOverride = ladderHold ? campaign?.ladderFixedAcos?.fixedTargetAcos : null
      const fixedAcos = (settingsAcos: number | undefined) =>
        ladderOverride && ladderOverride > 0 ? ladderOverride : settingsAcos || 0
      placement.marketPurchases4w = campaign?.marketPurchases4w ?? null

      const MIN_CAMPAIGN_OVER_MARKET_CVR_RELATIVE_DIFF = 0.1
      const isExact = campaign?.matchType === matchTypesFromAmazon.EXACT
      const maxCampaignCvr = isExact ? Math.max(campaign.totalCvr || 0, campaign.asinCvr || 0) : 0
      const relativeDiffOverMarket = campaign?.marketCvr
        ? (maxCampaignCvr - campaign.marketCvr) / campaign.marketCvr
        : 0
      const exceedsMarketByMinRelativeDiff =
        !!campaign?.marketCvr &&
        relativeDiffOverMarket >= MIN_CAMPAIGN_OVER_MARKET_CVR_RELATIVE_DIFF
      const cvr =
        isExact && campaign.asinCvr && campaign.marketCvr && exceedsMarketByMinRelativeDiff
          ? maxCampaignCvr
          : placement.cvr || 0
      if (placement.placement === placementsEnum.TOP_OF_SEARCH) {
        const dynamicACOS = targetAcos?.dynamicTOStargetAcos.find(
          (targetAcos) => targetAcos.to >= cvr && targetAcos.from <= cvr
        )
        placement.targetAcosMode = targetAcosMode(
          ladderHold,
          targetAcos?.dynamicTOSTarget,
          volumeOk
        )
        placement.targetAcos =
          placement.targetAcosMode === "dynamic"
            ? dynamicACOS?.Acos || 0
            : fixedAcos(targetAcos?.TOStargetAcos)
      }

      if (placement.placement === placementsEnum.REST_OF_SEARCH) {
        const dynamicACOS = targetAcos?.dynamicROStargetAcos.find(
          (targetAcos) => targetAcos.to >= cvr && targetAcos.from <= cvr
        )
        placement.targetAcosMode = targetAcosMode(
          ladderHold,
          targetAcos?.dynamicROSTarget,
          volumeOk
        )
        placement.targetAcos =
          placement.targetAcosMode === "dynamic"
            ? dynamicACOS?.Acos || 0
            : fixedAcos(targetAcos?.ROStargetAcos)
      }

      if (placement.placement === placementsEnum.PRODUCT_PAGE) {
        const dynamicACOS = targetAcos?.dynamicPPtargetAcos.find(
          (targetAcos) => targetAcos.to >= cvr && targetAcos.from <= cvr
        )
        placement.targetAcosMode = targetAcosMode(ladderHold, targetAcos?.dynamicPPTarget, volumeOk)
        placement.targetAcos =
          placement.targetAcosMode === "dynamic"
            ? dynamicACOS?.Acos || 0
            : fixedAcos(targetAcos?.PPtargetAcos)
      }

      if (placement.placement === placementsEnum.AUDIENCE) {
        const dynamicACOS = targetAcos?.dynamicADTargetACOS.find(
          (targetAcos) => targetAcos.to >= cvr && targetAcos.from <= cvr
        )
        placement.targetAcosMode = targetAcosMode(ladderHold, targetAcos?.dynamicADTarget, volumeOk)
        placement.targetAcos =
          placement.targetAcosMode === "dynamic"
            ? dynamicACOS?.Acos || 0
            : fixedAcos(targetAcos?.ADtargetAcos)
      }

      if (placement.placement === placementsEnum.AMAZON_BUSINESS) {
        const dynamicACOS = targetAcos?.dynamicABTargetACOS.find(
          (targetAcos) => targetAcos.to >= cvr && targetAcos.from <= cvr
        )
        placement.targetAcosMode = targetAcosMode(ladderHold, targetAcos?.dynamicABTarget, volumeOk)
        placement.targetAcos =
          placement.targetAcosMode === "dynamic"
            ? dynamicACOS?.Acos || 0
            : fixedAcos(targetAcos?.ABtargetAcos)
      }

      if (placement.placement === placementsEnum.FEATURED_VIDEOS) {
        const dynamicACOS = (targetAcos?.dynamicFVTargetACOS ?? []).find(
          (targetAcos) => targetAcos.to >= cvr && targetAcos.from <= cvr
        )
        placement.targetAcosMode = targetAcosMode(ladderHold, targetAcos?.dynamicFVTarget, volumeOk)
        placement.targetAcos =
          placement.targetAcosMode === "dynamic"
            ? dynamicACOS?.Acos || 0
            : fixedAcos(targetAcos?.FVtargetAcos)
      }
    }

    return placementsCopy
  }

  function determineTargetAcos(campaign: ICampaign | undefined): IDefaultTargetAcos | undefined {
    return findDefaultTargetAcos(campaign, campaignData.defaultTargetAcos)
  }

  /**
   * Anton 2026-10-19: the dynamic (CVR ladder) Target ACOS applies only to HIGH-VOLUME search terms —
   * campaign.marketPurchases4w (SQP whole-market purchases, last 4 complete weeks) >= the
   * Settings -> Other "min market purchases" (default 200; 0 = gate off). Unknown volume = low volume.
   */
  function hasDynamicAcosVolume(campaign: ICampaign | undefined): boolean {
    const minVolume =
      campaignData.dynamicAcosMinMarketPurchases ?? DEFAULT_DYNAMIC_ACOS_MIN_MARKET_PURCHASES
    const volume = campaign?.marketPurchases4w
    return minVolume <= 0 || (typeof volume === "number" && volume >= minVolume)
  }

  function calcNewBidAdjustmentForPlacement(
    placements: IPlacementDataTable[],
    minLimitedCPC: number,
    raiseGate = false
  ): IPlacementDataTable[] {
    const placementsCopy: IPlacementDataTable[] = copy(placements)
    const mains = placementsCopy.filter(isMainPlacement)
    // A7 applies to computed bids only; a manual Limited CPC edit passes raiseGate=false.
    const auxCap = raiseGate ? gatedAuxCap(placementsCopy) : null
    let auxBase = highestMain(mains.map((placement) => placement.limitedCPC))

    let thinCapped = false
    for (const placement of mains) {
      const pct = flooredModifierPct(placement.limitedCPC, minLimitedCPC)
      const gated =
        auxCap != null
          ? capModifierAtCpc(
              pct,
              minLimitedCPC,
              Math.max(auxCap, Number(placement.targetCPCAmazon) || 0)
            )
          : pct
      // computed bids only (raiseGate); a manual Limited CPC edit is never capped
      placement.newBidAdjustment = raiseGate
        ? capThinModifier(placement, gated, campaignData)
        : gated
      if (placement.newBidAdjustment !== gated) thinCapped = true
    }
    if ((auxCap != null || thinCapped) && minLimitedCPC > 0) {
      // aux modifiers sit on the highest REALIZED main bid (what Amazon multiplies).
      auxBase = highestMain(
        mains.map((placement) => minLimitedCPC * (1 + (placement.newBidAdjustment || 0) / 100))
      )
    }
    for (const placement of placementsCopy) {
      if (isMainPlacement(placement)) continue
      // FV stacks on the base bid; AUDIENCE / AB stack on the highest realized main bid.
      const pct = flooredModifierPct(
        placement.limitedCPC,
        raiseGate && isBaseStacked(placement) ? minLimitedCPC : auxBase
      )
      placement.newBidAdjustment = raiseGate ? capThinModifier(placement, pct, campaignData) : pct
    }

    return placementsCopy
  }

  function finalizeMetrics(
    placements: IPlacementDataTable[],
    campaignId: number
  ): IPlacementDataTable[] {
    const calculationLimitedCPC = calcLimitedCPCForPlacement(placements, campaignId)
    setMinLimitedCPCByCampaignIdMap((minLimitedCPCByCampaignIdMap) => {
      return {
        ...minLimitedCPCByCampaignIdMap,
        [String(campaignId)]: calculationLimitedCPC.minLimitedCPC,
      }
    })

    let placementsDataTable = calcBestCPCForPlacement(calculationLimitedCPC.placements)
    placementsDataTable = calcNewBidAdjustmentForPlacement(
      placementsDataTable,
      calculationLimitedCPC.minLimitedCPC,
      true
    )
    setPlacementsDataForUpdate((prev) => {
      return prev.map((placement) => {
        if (placement.campaignId === campaignId) {
          return {
            campaignId,
            placements: placementsDataTable,
          }
        }

        return placement
      })
    })

    selectCampaignIfNeeded({
      campaignId,
      placements: placementsDataTable,
      campaignData,
      setSelectedCampaignIds,
    })
    return placementsDataTable
  }

  function calcAllMetrics(
    placementSubResult: IPlacementData[],
    campaignId: number
  ): IPlacementDataTable[] {
    return finalizeMetrics(calcTargetACOS(placementSubResult), campaignId)
  }

  function calcMetricsBeforeUpdatedAcos(
    placementSubResult: IPlacementDataTable[],
    campaignId: number
  ): IPlacementDataTable[] {
    return finalizeMetrics(placementSubResult, campaignId)
  }

  return {
    calcBestCPCForPlacement,
    calcLimitedCPCForPlacement,
    selectCampaignIfNeeded,
    calcNewBidAdjustmentForPlacement,
    calcAllMetrics,
    calcTargetACOS,
    calcMetricsBeforeUpdatedAcos,
    capLimitedCPC,
  }
}

export default usePlacementMetrics
